import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Params, Router } from '@angular/router';
import { LocalStorageKeys, LocalStorageService } from '@iot-platform/core';
import { BusinessProfile } from '@iot-platform/models/common';
import { get } from 'lodash';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthFacade } from '../state/facades/auth.facade';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  private readonly router: Router = inject(Router);
  private readonly authFacade: AuthFacade = inject(AuthFacade);
  private readonly storage: LocalStorageService = inject(LocalStorageService);
  private readonly authService: AuthService = inject(AuthService);

  canActivate(next: ActivatedRouteSnapshot): Observable<boolean> {
    if ('refreshToken' in next.queryParams) {
      const { userId, businessProfileId } = this.collectRouteParams(next);
      return this.authService.signWithRefreshToken(next.queryParams.refreshToken, userId).pipe(
        map((success: boolean) => {
          if (!success) {
            this.signOut();
            return false;
          }
          this.authFacade.selectBusinessProfile({ id: businessProfileId }, false);
          return true;
        })
      );
    } else if (
      (this.storage.get(LocalStorageKeys.STORAGE_ID_TOKEN_KEY) || this.storage.get(LocalStorageKeys.STORAGE_SSO_TOKEN_ID)) &&
      this.hasStoredBusinessProfile()
    ) {
      return of(true);
    } else {
      this.signOut();
      return of(false);
    }
  }

  /** A stored business profile without `id` cannot feed the `business-profile-id` header: every scoped API call would fail. */
  private hasStoredBusinessProfile(): boolean {
    try {
      return !!JSON.parse(this.storage.get(LocalStorageKeys.STORAGE_BUSINESS_PROFILE_KEY))?.id;
    } catch {
      return false;
    }
  }

  private collectRouteParams(activatedRoute: ActivatedRouteSnapshot): Params {
    let route = activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return get(route, 'params', {});
  }

  private signOut(): void {
    this.authFacade.signOut();
    this.router.navigate(['/login']);
  }
}
